import moment from "moment";
import { buildListingRequestFields } from "./listingRequestEmail";
import { normalizeContact } from "./contactNormalize";
import { loadListingRequestRecipients } from "./notificationSettings";

// utils/formatNumber.js
export function formatNumberShort(value) {
  if (value === null || value === undefined) return "0";

  const absValue = Math.abs(value);
  let number,
    suffix = "";

  if (absValue >= 1_000_000_000) {
    number = value / 1_000_000_000;
    suffix = "B";
  } else if (absValue >= 1_000_000) {
    number = value / 1_000_000;
    suffix = "M";
  } else if (absValue >= 1_000) {
    number = value / 1_000;
    suffix = "k";
  } else {
    number = value;
  }

  // 👇 floor instead of round
  // number = Math.floor(number * 10) / 10;
  number = Math.floor(number * 100) / 100;

  return `${number % 1 === 0 ? number : number.toFixed(2)}${suffix}`;
}

export const formatNumber = (value) =>
  value ? Number(value).toLocaleString("en-US") : "";

// Google Places appends ", USA" to addresses — strip it for display/storage.
// Global, so addresses embedded mid-sentence (notifications etc.) get cleaned.
export const stripCountry = (s) =>
  typeof s === "string" ? s.replace(/,\s*(USA|United States)\b/gi, "") : s;

// Title Case for UI labels — capitalizes every significant word, keeping
// short connectors lowercase unless they're the first or last word.
// Leaves the rest of each word untouched so names like "McGregor" survive.
const TITLE_CONNECTORS = new Set([
  "a", "an", "the", "in", "of", "to", "for", "with", "and", "or", "at", "on", "by",
]);
export function titleCaseLabel(s) {
  const words = String(s || "").split(/\s+/).filter(Boolean);
  return words
    .map((w, i) => {
      const isConnector = TITLE_CONNECTORS.has(w.toLowerCase());
      if (i !== 0 && i !== words.length - 1 && isConnector) return w.toLowerCase();
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join(" ");
}

export const removeCommas = (value) => value.replace(/,/g, "");

export const formatCurrency = (value, currency = "USD") => {
  if (value === null || value === undefined || isNaN(value)) return "$0.00";

  return Number(value).toLocaleString("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const formatDate = (date, format = "MM-DD-YYYY") => {
  if (!date) return "-";
  return moment(date).format(format);
};

// utils/statusColors.js
export const getStatusColor = (status) => {
  switch (status) {
    case "Active":
      return "bg-emerald-500"; // bright green

    case "Under Contract":
      return "bg-sky-500"; // light blue

    case "Pending":
      return "bg-yellow-400"; // bright yellow

    case "Closed":
      return "bg-slate-500"; // neutral gray

    case "Cancelled":
      return "bg-rose-500"; // strong red/pink

    case "Expired":
      return "bg-orange-500"; // orange

    case "Prospect":
      return "bg-violet-500"; // purple

    case "On Hold":
      return "bg-amber-600"; // dark amber (different from yellow)

    case "CMAs":
      return "bg-indigo-600"; // deep indigo (different from blue)

    case "Listing Appointment":
      return "bg-fuchsia-500"; // pink-purple

    case "Pre-Market":
      return "bg-lime-500"; // lime green (different from emerald)

    case "Coming Soon":
      return "bg-cyan-500"; // cyan (different from sky)

    default:
      return "bg-gray-400";
  }
};

export const formatText = (value) => {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const newmessagesuccess = async (
  title,
  id,
  message,
  taskid,
  type,
  dealId,
) => {
  try {
    // const res = await fetch('https://us-central1-raftekpro-82bb0.cloudfunctions.net/api/newslot', {
    const res = await fetch(
      // "https://team-edelstein.web.app/newmessage",
      "https://us-central1-team-edelstein.cloudfunctions.net/api/newmessage",

      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          title: title,
          message: message,
          id: id,
          taskid: taskid,
          dealid: dealId,
          type: type,
        }),
      },
    );

    if (res.status === 200) {
      console.log("new message sent " + JSON.stringify(res));
      const data = await res.json();

      console.log("Successfully Notified User");
    } else {
      console.log("Notififcation Error" + JSON.stringify(res));
    }
  } catch (error) {
    console.error("newmessagesuccess error:", error);
  }
};

export const formatDates = (date) => {
  if (!date || date === "") return "";

  const [yyyy, mm, dd] = date?.split("-");
  return `${mm}/${dd}/${yyyy}`;
}

// ============ DEAL APPROVAL NOTIFICATIONS ============
// User type values: 0 active, 1 deleted, 2 disabled.

export function selectApprovers(users) {
  return (users || []).filter(
    (u) =>
      u?.type !== 1 &&
      u?.type !== 2 &&
      (u?.role === "Admin" || u?.role === "Supervisor"),
  );
}

// Client Care = Supervisor role. File requests are handled by Client Care only.
export function selectClientCare(users) {
  return (users || []).filter(
    (u) => u?.type !== 1 && u?.type !== 2 && u?.role === "Supervisor",
  );
}

export function selectAccountants(users) {
  return (users || []).filter(
    (u) => u?.type !== 1 && u?.type !== 2 && u?.role === "Accountant",
  );
}

// Stage 1: notify the right audience when a pending deal is submitted.
// Recipients and title vary by requestType:
//   - "saleTurnIn" → Admins + Client Care (Supervisors) review and approve
//   - file request (default) → Client Care only handles the file
// Safe to call even if there are no recipients (no-op).
export async function notifyApprovers(deal, allUsers, submitter) {
  const isSaleTurnIn = deal?.requestType === "saleTurnIn";
  const recipients = isSaleTurnIn
    ? selectApprovers(allUsers)
    : selectClientCare(allUsers);
  if (recipients.length === 0) return;
  const submitterName =
    `${submitter?.firstname || ""} ${submitter?.lastname || ""}`.trim() ||
    "An agent";
  const subject =
    deal?.propertyaddress ||
    (deal?.dealtype ? `${deal.dealtype} request` : "New deal");
  const message = `${submitterName} · ${subject}`;
  const dealRef = String(deal?.createdat || deal?.id || "");
  const title = isSaleTurnIn
    ? "New Deal Pending Approval"
    : "New Listing — File to Be Created";
  const notifType = isSaleTurnIn ? "pending-approval" : "pending-file";
  await Promise.all(
    recipients.map((u) =>
      newmessagesuccess(title, u.id, message, dealRef, notifType, dealRef),
    ),
  );
}

// Client Care needs to know whenever a deal is turned in — including when a
// Team Lead / Supervisor / Accountant turns one in, which auto-approves it and
// therefore never hits the "pending approval" path above. Only fires for
// saleTurnIn deals, and skips the submitter so they don't notify themselves.
export async function notifyClientCareTurnIn(deal, allUsers, submitter) {
  if (deal?.requestType !== "saleTurnIn") return;
  const recipients = selectClientCare(allUsers).filter(
    (u) => u?.id !== submitter?.id,
  );
  if (recipients.length === 0) return;
  const submitterName =
    `${submitter?.firstname || ""} ${submitter?.lastname || ""}`.trim() ||
    "A team lead";
  const subject =
    deal?.propertyaddress ||
    (deal?.dealtype ? `${deal.dealtype} deal` : "a deal");
  const message = `${submitterName} turned in ${subject}`;
  const dealRef = String(deal?.createdat || deal?.id || "");
  await Promise.all(
    recipients.map((u) =>
      newmessagesuccess(
        "New Deal Turned In",
        u.id,
        message,
        dealRef,
        "deal-turned-in",
        dealRef,
      ),
    ),
  );
}

// Single entry point for post-submit notifications, so every turn-in path stays
// consistent. Pending submissions go to the approver / file-request audience
// (unchanged). An already-approved turn-in — e.g. a Team Lead turning in their
// own deal, which used to notify no one — informs Client Care instead.
export async function notifyOnDealSubmit(deal, allUsers, submitter) {
  // Role-based in-app alerts: the approval queue (pending) or Client Care
  // (already-approved turn-in).
  if (deal?.status === "Pending") {
    await notifyApprovers(deal, allUsers, submitter);
  } else {
    await notifyClientCareTurnIn(deal, allUsers, submitter);
  }
  // Sale turn-ins also email the configured Request-Notification list for the
  // matching side (Settings → Notifications), so the office triage recipients
  // are looped in just like they are for new requests. Fire-and-forget.
  if (deal?.requestType === "saleTurnIn") {
    emailListingRequestRecipients(deal, submitter, { event: "turnIn" }).catch(
      (e) => console.error("Turn-in recipient email failed:", e),
    );
  }
}

// Notify when a brand-new pipeline lead is created. Recipients: the lead's
// assigned agent (if any) plus the configured Settings -> Notifications list
// for that side (seller/buyer) — the office triage queue. The person who
// created the lead is never notified about their own add, and deleted/disabled
// users are skipped. Reuses the existing /newmessage endpoint (no backend
// deploy). Safe no-op when there are no recipients. `side` is "seller" | "buyer".
export async function notifyOnNewLead(lead, allUsers, side) {
  const creatorId = lead?.createdby || "";
  const isBuyer = side === "buyer";
  let lists = { seller: [], buyer: [] };
  try {
    lists = await loadListingRequestRecipients();
  } catch (e) {
    /* no central list configured — fall back to assigned agent only */
  }
  const ids = new Set();
  if (lead?.assignedAgent) ids.add(lead.assignedAgent);
  (isBuyer ? lists.buyer : lists.seller).forEach((id) => ids.add(id));
  const targetIds = [...ids].filter((id) => {
    if (!id || id === creatorId) return false;
    const u = (allUsers || []).find((x) => x?.id === id);
    return u && u.type !== 1 && u.type !== 2;
  });
  if (!targetIds.length) return;
  const title = isBuyer ? "New Buyer Lead" : "New Listing Lead";
  const subject =
    lead?.contactName ||
    lead?.propertyAddress ||
    (isBuyer ? "New buyer lead" : "New listing lead");
  // The /newmessage endpoint requires non-empty taskid + dealid — use the lead id.
  const ref = String(lead?.id || lead?.createdat || Date.now());
  await Promise.all(
    targetIds.map((id) =>
      newmessagesuccess(title, id, subject, ref, "new-lead", ref),
    ),
  );
}

// Notify when a deal reaches a milestone stage. Recipients: the deal's assigned
// agent, plus Client Care (Supervisors) when a listing goes live / under
// contract (so the file gets built) and Accountants when it closes. The actor
// who made the change isn't notified about their own action. Reuses the
// existing endpoint. Safe no-op for non-milestone stages or no recipients.
const MILESTONE_LABELS = {
  "Pre-Market": "Listing Signed",
  "Coming Soon": "Listing Coming Soon",
  "Under Contract": "Deal Under Contract",
  Closed: "Deal Closed",
};
export async function notifyDealMilestone(deal, newStage, allUsers, actor) {
  const label = MILESTONE_LABELS[newStage];
  if (!label) return; // not a milestone we push on
  const actorId = actor?.id || "";
  const ids = new Set();
  if (deal?.agentData) ids.add(deal.agentData);
  if (["Pre-Market", "Coming Soon", "Under Contract"].includes(newStage))
    selectClientCare(allUsers).forEach((u) => ids.add(u.id));
  if (newStage === "Closed")
    selectAccountants(allUsers).forEach((u) => ids.add(u.id));
  const targetIds = [...ids].filter((id) => {
    if (!id || id === actorId) return false;
    const u = (allUsers || []).find((x) => x?.id === id);
    return u && u.type !== 1 && u.type !== 2;
  });
  if (!targetIds.length) return;
  const subject =
    deal?.propertyaddress || deal?.clientLastName || "Deal updated";
  const ref = String(deal?.createdat || deal?.id || Date.now());
  await Promise.all(
    targetIds.map((id) =>
      newmessagesuccess(label, id, subject, ref, "deal-milestone", ref),
    ),
  );
}

// Notify the requesting agent when their file request has been completed by Client Care.
// Safe to call even if the recipient lookup fails (no-op).
export async function notifyAgentOnFileComplete(deal, allUsers, completer) {
  const recipientId = deal?.createdby;
  if (!recipientId) return;
  const recipient = (allUsers || []).find((u) => u?.id === recipientId);
  if (!recipient || recipient.type === 1 || recipient.type === 2) return;
  const completerName =
    `${completer?.firstname || ""} ${completer?.lastname || ""}`.trim() ||
    "Client Care";
  const subject = deal?.propertyaddress || "Your file";
  const message = `${subject} · completed by ${completerName}`;
  const dealRef = String(deal?.createdat || deal?.id || "");
  await newmessagesuccess(
    "Your file is ready",
    recipientId,
    message,
    dealRef,
    "file-completed",
    dealRef,
  );
}

// Notify the approving LC (or the admin/supervisor fallback queue) that an agent
// requested an up-for-grabs open house. `opportunity.approverLcId` was resolved
// at post time; when blank we notify active Admins + Supervisors.
export async function notifyLcOnOhRequest(opportunity, request, allUsers) {
  const recipientIds = opportunity?.approverLcId
    ? [opportunity.approverLcId]
    : selectApprovers(allUsers).map((u) => u.id);
  const targetIds = recipientIds.filter((id) => {
    const u = (allUsers || []).find((x) => x?.id === id);
    return u && u.type !== 1 && u.type !== 2;
  });
  if (!targetIds.length) return;
  const addr = opportunity?.propertyAddress?.split(",")[0] || "a listing";
  const title = "Open House Request to Approve";
  const message = `${request?.agentName || "An agent"} wants to host ${addr}`;
  const ref = String(opportunity?.id || Date.now());
  await Promise.all(
    targetIds.map((id) => newmessagesuccess(title, id, message, ref, "oh-request", ref)),
  );
}

// Notify a requesting agent that their request was approved or declined.
// `decision` is "approved" | "declined".
export async function notifyAgentOnOhDecision(opportunity, request, decision) {
  const agentId = request?.agentId;
  if (!agentId) return;
  const addr = opportunity?.propertyAddress?.split(",")[0] || "the listing";
  const approved = decision === "approved";
  const title = approved ? "You're Hosting an Open House" : "Open House Request Update";
  const message = approved
    ? `You're approved to host ${addr}`
    : `Your request for ${addr} wasn't selected this time`;
  const ref = String(opportunity?.id || Date.now());
  await newmessagesuccess(
    title,
    agentId,
    message,
    ref,
    approved ? "oh-approved" : "oh-declined",
    ref,
  );
}

// Email the agent listed on a file (deal.agentData) when Client Care marks it
// complete. Additive to notifyAgentOnFileComplete, which sends an in-app
// notification to the submitter (deal.createdby). The Cloud Function sends the
// email AS the completer, so the completer must have a connected Gmail token.
//
// Returns a result the caller uses to pick a toast:
//   { ok: true, gmailMessageId? }
//   { ok: false, reason: "no-recipient" }       // no agentData / not found / no email
//   { ok: false, reason: "no-gmail" }            // completer has no gmail token
//   { ok: false, reason: "send-failed", error }  // endpoint returned an error
export async function emailAgentOnFileComplete(deal, allUsers, completer) {
  const agentId = deal?.agentData;
  if (!agentId) return { ok: false, reason: "no-recipient" };

  const agent = (allUsers || []).find((u) => u?.id === agentId);
  if (!agent || !agent.email) return { ok: false, reason: "no-recipient" };

  if (!completer?.gmailtoken?.access_token) {
    return { ok: false, reason: "no-gmail" };
  }

  const { sendOpenHouseEmail } = await import("./emailSender");

  const agentFirst = agent.firstname || "there";
  const agentFullName =
    `${agent.firstname || ""} ${agent.lastname || ""}`.trim() || "Agent";
  const completerName =
    `${completer?.firstname || ""} ${completer?.lastname || ""}`.trim() ||
    "Client Care";
  const address = deal?.propertyaddress || "";
  const subject = address
    ? `File Complete — ${address}`
    : "Your File Is Complete";
  const propertyPhrase = address ? `your file for ${address}` : "your file";

  const text = [
    `Hi ${agentFirst},`,
    ``,
    `Good news — ${propertyPhrase} has been completed by ${completerName}.`,
    `No action is needed on your end.`,
    ``,
    `— Team Edelstein`,
  ].join("\n");

  const html =
    `<p>Hi ${agentFirst},</p>` +
    `<p>Good news — ${propertyPhrase} has been completed by ${completerName}.</p>` +
    `<p>No action is needed on your end.</p>` +
    `<p>— Team Edelstein</p>`;

  const result = await sendOpenHouseEmail({
    userId: completer.id,
    to: agent.email,
    toName: agentFullName,
    subject,
    text,
    html,
    type: "file-completed",
    relatedDealId: String(deal?.createdat || deal?.id || ""),
  });

  if (result?.ok) return { ok: true, gmailMessageId: result.gmailMessageId };
  return { ok: false, reason: "send-failed", error: result?.error };
}

// Email the configured "listing/buyer request" recipients (set in Settings →
// Notifications) when a new request is submitted. Independent of and
// additional to the in-app notifyApprovers flow.
export async function emailListingRequestRecipients(deal, submitter, opts = {}) {
  // Lazy imports to avoid a circular dependency (helper.js is widely imported).
  const { loadListingRequestRecipients } = await import(
    "./notificationSettings"
  );
  const { sendOpenHouseEmail } = await import("./emailSender");
  const { getAllusers, getContactsByIds } = await import(
    "./firebasefunctions"
  );

  try {
    const lists = await loadListingRequestRecipients();
    const isBuyer = deal?.dealtype === "Buyer";
    const isTurnIn = opts.event === "turnIn";
    // Turn-ins email the whole staff group (both sides combined), so a blank
    // per-side list can never silently drop the notification. Requests stay
    // per-side (buyer requests → buyer list, listing requests → seller list).
    const userIds = isTurnIn
      ? [...new Set([...(lists.buyer || []), ...(lists.seller || [])])]
      : isBuyer
        ? lists.buyer
        : lists.seller;
    if (!userIds || userIds.length === 0) return;

    const allUsers = await getAllusers("users");
    const recipients = (allUsers || []).filter(
      (u) =>
        userIds.includes(u.id) &&
        u.type !== 1 &&
        u.type !== 2 &&
        u.email,
    );
    if (recipients.length === 0) return;

    const submitterName =
      `${submitter?.firstname || ""} ${submitter?.lastname || ""}`.trim() ||
      "An agent";
    const agent = (allUsers || []).find((u) => u?.id === deal?.agentData);
    const agentName = agent
      ? `${agent.firstname || ""} ${agent.lastname || ""}`.trim()
      : "";
    const address = deal?.propertyaddress || "(no address provided)";
    const eventLabel = isTurnIn
      ? "Sale Turn-In"
      : isBuyer
        ? "Buyer Request"
        : "Listing Request";
    // Buyer requests have no property yet — anchor the subject on the client
    // name instead of "(no address provided)".
    const subject = `New ${eventLabel} — ${
      deal?.propertyaddress || deal?.clientLastName || address
    }`;

    // Resolve attached clients (deal.customers = contact ids) to name/phone/
    // email for the body. Best-effort: on failure we still send every other
    // field rather than dropping the whole email.
    let clients = [];
    try {
      const ids = (deal?.customers || []).filter(Boolean);
      if (ids.length > 0) {
        const raw = await getContactsByIds(ids);
        clients = (raw || []).map((c) => normalizeContact(c)).filter(Boolean);
      }
    } catch (clientErr) {
      console.error("Request email: failed to resolve clients:", clientErr);
    }

    let lines;
    let htmlLines;
    if (isTurnIn) {
      // Turn-ins carry sale fields, not request-intake fields — keep the email
      // short: who turned in what, for whom.
      const clientName = clients
        .map((c) => c?.name)
        .filter(Boolean)
        .join(" & ");
      const sideWord = isBuyer ? "buyer-side" : "listing-side";
      lines = [
        `${submitterName} just turned in a ${sideWord} sale.`,
        ``,
        `Address: ${address}`,
        clientName ? `Client: ${clientName}` : null,
        agentName ? `Agent: ${agentName}` : null,
        ``,
        `Open in the CRM to review the deal.`,
      ].filter((l) => l !== null);
      htmlLines = [
        `<p>${submitterName} just turned in a ${sideWord} sale.</p>`,
        `<ul>`,
        `<li><strong>Address:</strong> ${address}</li>`,
        clientName ? `<li><strong>Client:</strong> ${clientName}</li>` : "",
        agentName ? `<li><strong>Agent:</strong> ${agentName}</li>` : "",
        `</ul>`,
        `<p>Open in the CRM to review the deal.</p>`,
      ].filter(Boolean);
    } else {
      // Ordered [{ label, value }] rows — every filled-in intake field, blanks
      // omitted. formatDate is this file's own moment-based formatter.
      const fields = buildListingRequestFields({
        deal,
        clients,
        agentName,
        formatDate,
      });
      lines = [
        `${submitterName} just submitted a new ${eventLabel}.`,
        ``,
        ...fields.map((f) => `${f.label}: ${f.value}`),
        ``,
        `Open in the CRM to start the file.`,
      ];
      htmlLines = [
        `<p>${submitterName} just submitted a new ${eventLabel}.</p>`,
        `<ul>`,
        ...fields.map((f) => `<li><strong>${f.label}:</strong> ${f.value}</li>`),
        `</ul>`,
        `<p>Open in the CRM to start the file.</p>`,
      ];
    }
    const body = lines.join("\n");
    const html = htmlLines.join("");

    await Promise.all(
      recipients.map((r) =>
        sendOpenHouseEmail({
          userId: submitter?.id,
          to: r.email,
          toName: `${r.firstname || ""} ${r.lastname || ""}`.trim(),
          subject,
          text: body,
          html,
          type: isTurnIn ? "sale-turnin-notification" : "listing-request-notification",
          relatedDealId: deal?.createdat || deal?.id || "",
        }).catch((err) =>
          console.error("Listing request email failed:", r.email, err),
        ),
      ),
    );
  } catch (err) {
    console.error("emailListingRequestRecipients failed:", err);
  }
}

// Stage 2: notify Accountants when a deal transitions to Approved.
// Caller must guard with the prior-status check (see plan tasks 8 and 9).
export async function notifyAccountantsOnApproval(deal, allUsers, approver) {
  const recipients = selectAccountants(allUsers);
  if (recipients.length === 0) return;
  const approverName =
    `${approver?.firstname || ""} ${approver?.lastname || ""}`.trim() ||
    "An approver";
  const subject = deal?.propertyaddress || "Deal";
  const message = `${subject} · approved by ${approverName}`;
  const dealRef = String(deal?.createdat || deal?.id || "");
  await Promise.all(
    recipients.map((u) =>
      newmessagesuccess(
        "Deal ready for accounting review",
        u.id,
        message,
        dealRef,
        "approved-for-accounting",
        dealRef,
      ),
    ),
  );
}
